/**
 * «Copertura dati» — brief part 1 §6 band 3 (right).
 *
 * The designer's table: Feed · Ultimo invio · Stato, 40px rows under a 2px ink rule. Its rows are
 * the FEEDS of the run's market, from `GET /foundations/feeds` (the Data ▸ Feeds page's own read,
 * `useCockpitReads`): the feed, when its batch in force landed, and the platform's own freshness
 * verdict against the feed's contract. Nothing here judges a feed: an undeclared contract is "not
 * judged", never fresh (ADR 2006), and a verdict this version does not know is printed as sent.
 *
 * It replaced "Data behind this page", a strip of chips over the reads THIS page makes. What that
 * strip said about a read that failed, was refused or is not published is now said once, in the
 * page's status strip (brief §4); a read that answered empty says so where its figure would stand.
 *
 * At most `FEED_ROWS` rows, the feeds that need attention first; the rest are one click away on
 * Data ▸ Feeds, opened on the same market.
 */

import { useTranslation } from 'react-i18next';

import type { FeedStatus } from '@/api/generated';
import { when } from '@/features/foundations/schedules-panel';
import { IdTag, type ReadState } from '@/planning';
import { FOCUS_RING } from '@/primitives';
import { feedWords } from '@/planning/words';

/**
 * As many rows as "Needs a decision" can have (`DECISION_ROWS`), so the two columns of the band
 * end together; the whole list is Data ▸ Feeds.
 */
export const FEED_ROWS = 7;

type Verdict = 'fresh' | 'late' | 'neverLanded' | 'notJudged' | 'notPublished' | 'other';

/** The platform's verdict, as the row states it. `blocking` is the contract's threshold exceeded. */
export function feedVerdict(feed: Pick<FeedStatus, 'freshnessState' | 'blocking'>): Verdict {
  if (feed.blocking === true || feed.freshnessState === 'stale') return 'late';
  switch (feed.freshnessState) {
    case undefined: return 'notPublished';
    case 'fresh': return 'fresh';
    case 'no_batch': return 'neverLanded';
    case 'undeclared': return 'notJudged';
    default: return 'other';
  }
}

/** Needs attention first, then the rest; by feed within each — a stable order. */
export function coverageRows(feeds: readonly FeedStatus[]): readonly FeedStatus[] {
  const rank = (feed: FeedStatus): number => {
    const verdict = feedVerdict(feed);
    return verdict === 'late' || verdict === 'neverLanded' ? 0 : verdict === 'fresh' ? 2 : 1;
  };
  return [...feeds].sort((a, b) => rank(a) - rank(b) || a.feedId.localeCompare(b.feedId));
}

/** Glyph, ink and words per verdict — never the colour alone (design/09 §12.6). */
const VERDICT: Readonly<Record<Verdict, { readonly glyph: string; readonly ink: string; readonly key: string }>> = {
  fresh: { glyph: '✓', ink: 'text-status-good-strong', key: 'overview.feeds.state.fresh' },
  late: { glyph: '!', ink: 'text-status-critical-strong', key: 'overview.feeds.state.late' },
  neverLanded: { glyph: '!', ink: 'text-status-critical-strong', key: 'overview.feeds.state.neverLanded' },
  notJudged: { glyph: '◐', ink: 'text-ink-label', key: 'overview.feeds.state.notJudged' },
  notPublished: { glyph: '—', ink: 'text-ink-label', key: 'overview.feeds.state.notPublished' },
  other: { glyph: '◐', ink: 'text-ink-label', key: 'overview.feeds.state.other' },
};

const COLUMN_HEAD = 'text-ink-primary px-[10px] py-2 text-start text-[11px] font-bold uppercase tracking-[0.06em]';

export interface DataCoverageProps {
  /** The market whose feeds are read — the run on screen's; `null` while none is known. */
  readonly country: string | null;
  readonly state: ReadState<readonly FeedStatus[]> | null;
  readonly feeds: readonly FeedStatus[];
  /** Open Data ▸ Feeds on the market. */
  readonly onOpenAll: () => void;
}

export function DataCoverage({ country, state, feeds, onOpenAll }: DataCoverageProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const rows = coverageRows(feeds).slice(0, FEED_ROWS);

  const message = country === null || state === null
    ? t('overview.feeds.noRun')
    : state.kind === 'loading'
      ? t('state.loading')
      : state.kind === 'unavailable'
        ? t('overview.feeds.unavailable')
        : state.kind === 'awaiting'
          ? t('overview.feeds.none', { market: country })
          : null;

  return (
    <section aria-labelledby="cockpit-coverage" className="flex min-w-0 grow flex-col" data-coverage="">
      <h2 id="cockpit-coverage" className="text-ink-primary font-display pb-2 text-section font-bold uppercase">
        {t('overview.coverage.title')}
      </h2>
      {message !== null ? (
        <p className="text-body text-ink-secondary border-ink-primary border-t-2 py-3">{message}</p>
      ) : (
        <>
          <table className="w-full table-fixed border-collapse text-[13px]">
            <colgroup>
              <col />
              <col className="w-[120px]" />
              <col className="w-[130px]" />
            </colgroup>
            <thead>
              <tr className="border-ink-primary border-b border-t-2">
                <th scope="col" className={COLUMN_HEAD}>{t('overview.feeds.column.feed')}</th>
                <th scope="col" className={COLUMN_HEAD}>{t('overview.feeds.column.lastLanded')}</th>
                <th scope="col" className={COLUMN_HEAD}>{t('overview.feeds.column.state')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((feed) => {
                const verdict = VERDICT[feedVerdict(feed)];
                const landed = when(feed.landedAt, i18n.language);
                const words = t(verdict.key, { state: feed.freshnessState ?? '' });
                return (
                  <tr key={feed.feedId} className="border-hairline h-[40px] border-b" data-coverage-feed={feed.feedId}>
                    {/* The feed in words (audit A5); its id is behind the «id», for whoever has to ask for it (audit 3). */}
                    <td className="text-ink-primary px-[10px]">
                      <span className="inline-flex flex-wrap items-baseline gap-1">
                        <span>{feedWords(t, feed.feedId)}</span>
                        <IdTag id={feed.feedId} kind={t('words.feed.kind')} />
                      </span>
                    </td>
                    {/* Wraps rather than cuts (usability audit D4): «2 oct 2026, 8:14 p.m.» is wider
                        than the column, and a cut time reads as another time. */}
                    <td className="font-numeric text-ink-label px-[10px] text-[12px] leading-4 tabular-nums">{landed ?? t('absent.notStated')}</td>
                    <td className={`${verdict.ink} truncate px-[10px] font-semibold`} title={words}>
                      <span className="flex items-center gap-[6px]">
                        <span aria-hidden className="font-numeric">{verdict.glyph}</span>
                        <span className="truncate">{words}</span>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {feeds.length > rows.length && (
            <button
              type="button"
              onClick={onOpenAll}
              className={`text-caption text-ink-primary self-start pt-2 underline-offset-2 hover:underline ${FOCUS_RING}`}
            >
              {t('overview.feeds.all', { count: feeds.length })}
            </button>
          )}
        </>
      )}
    </section>
  );
}
